package com.exura.pages.login

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.exura.features.localization.commonLocalization
import com.exura.features.newauth.domain.manager.AuthManager
import com.exura.navigation.Route
import com.exura.util.StringValidator
import com.varabyte.kobweb.core.PageContext

sealed interface LoginEvent {
    data class EmailChanged(val email: String) : LoginEvent
    data class PasswordChanged(val password: String) : LoginEvent
    data object LoginClicked : LoginEvent
    data object ForgotPasswordClicked : LoginEvent
    data object NavigateToRegister : LoginEvent
}

data class LoginUiState(
    val email: String = "",
    val emailError: String = "",
    val password: String = "",
    val passwordError: String = "",
    val isLoading: Boolean = false,
)

class LoginController(
    private val context: PageContext,
    private val authManager: AuthManager,
) {
    var uiState by mutableStateOf(LoginUiState())
        private set

    suspend fun handleEvent(event: LoginEvent) {
        when (event) {
            is LoginEvent.EmailChanged -> onEmailChanged(event.email)
            is LoginEvent.PasswordChanged -> onPasswordChanged(event.password)
            is LoginEvent.LoginClicked -> onLoginClicked()
            is LoginEvent.ForgotPasswordClicked -> onForgotPasswordClicked()
            is LoginEvent.NavigateToRegister -> onNavigateToRegister()
        }
    }

    private fun onEmailChanged(email: String) {
        uiState = uiState.copy(
            email = email,
            emailError = ""
        )
    }

    private fun onPasswordChanged(password: String) {
        uiState = uiState.copy(
            password = password,
            passwordError = ""
        )
    }

    private suspend fun onLoginClicked() {
        val emailError = StringValidator.getEmailError(uiState.email)
        val passwordError = when {
            uiState.password.isBlank() -> loginLocalization.current.errors.passwordCantBeEmpty
            else -> ""
        }

        uiState = uiState.copy(
            emailError = emailError,
            passwordError = passwordError
        )

        if (emailError.isEmpty() && passwordError.isEmpty()) {
            uiState = uiState.copy(isLoading = true)
            authManager.signIn(
                email = uiState.email,
                password = uiState.password,
            )
            uiState = uiState.copy(isLoading = false)
        }
    }

    private fun onForgotPasswordClicked() {
        js("window.open('mailto:kontakt@exura.app', '_blank')") as Unit
    }

    private fun onNavigateToRegister() {
        context.router.navigateTo(Route.REGISTER)
    }
}

@Composable
fun rememberLoginController(
    context: PageContext,
    authManager: AuthManager,
): LoginController {
    return remember(context, authManager) {
        LoginController(
            context = context,
            authManager = authManager,
        )
    }
}
